import { useEffect, useState } from 'react'

import {
    ChakraProvider,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner,
    useDisclosure,
    Text,
    Flex,
    extendTheme,
} from '@chakra-ui/react'
import { ulid } from 'ulid'

import { FormComponent } from '../form'
import { GET_FORM_FIELDS } from './form_fields'
import { decodeJWTToken, sleep } from '../helpers'
import { Api } from '../../api'

import {
    TokenData,
    RequestPayload,
    Props,
    Outputs,
    File as FileInterface,
    Blob as BlobInterface,
    BlobUnparsed,
    Collection,
} from './interfaces'

const Loader = () => (
    <Spinner
        thickness="6px"
        size="xl"
        style={{
            position: 'absolute',
            top: 'calc(50% - 4em)',
            left: 'calc(50% - 4em)',
        }}
    />
)
const colors = {
    brand: {
        black: '#1C1C1E',
        grey: '#B8B8C2',
        inputHoverGrey: '#9C9CAB',
        lowerBlue: '#001CDB',
        lightBlue: '#0064FF',
        green: '#00D200',
        purple: '#6236FF',
        errorRed: '#E80000',
        backgroundColor: '#ECEFFF',
    },
}

const theme = extendTheme({
    colors,
    components: {
        Button: {
            variants: {
                staircase: {
                    bg: 'brand.lowerBlue',
                    minWidth: '160px',
                    borderRadius: 0,
                    color: 'white',
                    _disabled: {
                        bg: 'brand.lowerBlue',
                    },
                    _hover: {
                        bg: 'white',
                        border: '1px solid #001CDB',
                        color: 'brand.lowerBlue',
                    },
                },
            },
        },
    },
})

export const WidgetComponent = (props: Props) => {
    const { isOpen, onOpen, onClose } = useDisclosure()

    const { token, onComplete, onError } = props

    const [jobData, setJobData] = useState<{
        outputs: Outputs
        current_step: string
        status: string
        request_payload: RequestPayload
        error?: string
    }>({
        outputs: {
            GenerateBlobs: [],
        },
        current_step: '',
        status: '',
        request_payload: {
            styles: {},
        },
    })

    const [tokenData, setTokenData] = useState<TokenData>()
    const [blobs, setBlobs] = useState<BlobInterface[]>([])
    const [collections, setCollections] = useState<Collection[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [dataSent, setDataSent] = useState(false)
    const [state, setState] = useState<string | undefined>()
    const [files, setFiles] = useState<File[]>([])

    useEffect(() => {
        const decodedTokenData: any = decodeJWTToken(token)
        try {
            const {
                origin: decoded_origin,
                api_key: decoded_api_key,
                job_name: decoded_job_name,
                execution_id: decoded_execution_id,
            } = decodedTokenData
            setTokenData({
                origin: decoded_origin,
                api_key: decoded_api_key,
                job_name: decoded_job_name,
                execution_id: decoded_execution_id,
            })
        } catch (err) {
            console.log(err)
            onError(err)
            return
        }
    }, [])

    useEffect(() => {
        if (tokenData) {
            onOpen()
            initCheckInvocation().then()
        }
    }, [tokenData])

    useEffect(() => {
        if (state) {
            if (state === 'BlobsUploadWaitState') {
                const blobsUnparsed: BlobUnparsed[] =
                    jobData.outputs.GenerateBlobs
                const newBlobs: BlobInterface[] = []
                blobsUnparsed.forEach(
                    ({
                        response_payload: {
                            blob_id,
                            presigned_urls,
                            blob_name,
                        },
                    }) =>
                        newBlobs.push({
                            blob_id,
                            blob_name,
                            upload_url: presigned_urls.upload.url,
                        })
                )
                setBlobs(newBlobs)
            } else if (state === 'SUCCEEDED') {
                onClose()
                onComplete('SUCCEEDED')
            } else if (state === 'Execution failed') {
                onClose()
                onError('Execution failed')
            }
        }
    }, [state])

    useEffect(() => {
        if (files) {
            handleFilesInput().then()
        }
    }, [files])

    useEffect(() => {
        if (blobs) {
            handleFilesUpload().then()
        }
    }, [blobs])

    useEffect(() => {
        if (collections) {
            finishWidgetWork().then()
        }
    }, [collections])

    const finishWidgetWork = async () => {
        if (!tokenData) {
            return
        }
        const { origin, api_key, job_name, execution_id } = tokenData
        const api = new Api(origin, api_key)
        try {
            await api.resumeJob(job_name, execution_id, collections)
        } catch (err) {
            console.log({ error: err })
            onClose()
            onError('Error uploading file')
        }
        await initCheckInvocation()
    }

    const handleFilesUpload = async () => {
        if (!tokenData) {
            return
        }
        if (files.length !== blobs.length) {
            onClose()
            onError('Error at server side. Please, contact your manager')
            return
        }
        const { origin, api_key } = tokenData
        const api = new Api(origin, api_key)
        const newCollections: Collection[] = []
        for (const { blob_id, upload_url, blob_name } of blobs) {
            const fileIndex = files.findIndex((f) => f.name === blob_name)
            const file = files[fileIndex]
            try {
                await api.uploadFile(upload_url, file)
                const collection = {
                    data: {
                        documents: [
                            {
                                '@type': 'document',
                                '@id': ulid(),
                                has_document_name: {
                                    has_value: file.name,
                                },
                                has_staircase_blob_identifier: {
                                    has_value: blob_id,
                                },
                                has_document_mime_type: {
                                    has_value: file.type,
                                },
                            },
                        ],
                    },
                    metadata: {
                        version: 2,
                        validation: true,
                    },
                }
                newCollections.push(collection)
            } catch (err) {
                console.log({ error: err })
                onClose()
                onError('Error uploading file')
                return
            }
        }
        setCollections(newCollections)
    }

    const handleFilesInput = async () => {
        if (!tokenData) {
            return
        }
        const { origin, api_key, job_name, execution_id } = tokenData
        const api = new Api(origin, api_key)

        const resumeData: FileInterface[] = []

        files.forEach(({ name }: File) => {
            const index = name.lastIndexOf('.')
            const extension = name.slice(index)
            resumeData.push({
                blob_name: name,
                extension,
            })
        })
        await api.resumeJob(job_name, execution_id, resumeData)
        await initCheckInvocation()
    }

    const handleFormComplete = async (values: { files: File[] }) => {
        const { files } = values
        setDataSent(true)
        setFiles(files)
    }

    const initCheckInvocation = async () => {
        setIsLoading(true)
        while (true) {
            const isFinished: string | undefined = await checkInvocationStatus()
            if (isFinished) {
                setState(isFinished)
                break
            }
            await sleep(1500)
        }
        setIsLoading(false)
    }

    const checkInvocationStatus = async () => {
        if (!tokenData) {
            return
        }
        const { job_name, execution_id, origin, api_key } = tokenData
        const api = new Api(origin, api_key)
        const { status, current_step, outputs, error, request_payload } =
            await api.getJobExecutionDetails(job_name, execution_id)
        setJobData({
            outputs,
            current_step,
            error,
            status,
            request_payload,
        })
        switch (status) {
            case 'SUCCEEDED':
                return 'SUCCEEDED'
            case 'FAILED':
                return 'Execution failed'
            case 'WAIT_FOR_ACTION':
                return current_step
            default:
                if (error) {
                    return 'Execution failed'
                }
                return
        }
    }

    return (
        <ChakraProvider theme={theme}>
            {isLoading && !dataSent ? (
                <Loader />
            ) : tokenData && jobData?.request_payload?.styles ? (
                <Modal
                    closeOnOverlayClick={false}
                    isOpen={isOpen}
                    onClose={onClose}
                    isCentered
                    motionPreset="scale"
                    size={'sm'}
                >
                    <ModalOverlay backdropFilter="blur(10px) hue-rotate(90deg)" />
                    <ModalContent
                        sx={jobData?.request_payload?.styles?.root}
                        borderRadius={0}
                    >
                        <ModalHeader>
                            <Text
                                sx={jobData?.request_payload?.styles?.title}
                                fontWeight={'bold'}
                            >
                                Staircase Persistence
                            </Text>
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <Flex
                                w="100%"
                                minH="360px"
                                flexDir="column"
                                alignItems="flex-start"
                                justifyContent="flex-start"
                            >
                                {state === 'WaitState' ? (
                                    <FormComponent
                                        fields={GET_FORM_FIELDS()}
                                        onFormComplete={handleFormComplete}
                                        styles={
                                            jobData?.request_payload?.styles
                                        }
                                    />
                                ) : (
                                    <Spinner thickness="6px" size="xl" />
                                )}
                            </Flex>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            ) : undefined}
        </ChakraProvider>
    )
}
